// @flow

import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { i18n, getCompanyCurrency } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import PostJournalEntryMutation from 'main-app/mutations/PostJournalEntry';
import VoidJournalEntryMutation from 'main-app/mutations/VoidJournalEntry';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #333;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ status }) => {
    switch (status) {
      case 'DRAFT': return '#FFF3E0';
      case 'POSTED': return '#E8F5E9';
      case 'VOIDED': return '#FFEBEE';
      default: return '#F5F5F5';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'DRAFT': return '#E65100';
      case 'POSTED': return '#2E7D32';
      case 'VOIDED': return '#C62828';
      default: return '#616161';
    }
  }};
`;

const LinesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  border-bottom: 2px solid #eee;
  &:last-child,
  &:nth-child(3),
  &:nth-child(4) {
    text-align: right;
  }
`;

const Td = styled.td`
  padding: 8px 12px;
  font-size: 13px;
  border-bottom: 1px solid #f5f5f5;
  color: #333;
  &:last-child,
  &:nth-child(3),
  &:nth-child(4) {
    text-align: right;
    font-family: 'SF Mono', 'Menlo', monospace;
  }
`;

const TotalRow = styled.tr`
  font-weight: 700;
  td {
    border-top: 2px solid #eee;
    padding-top: 10px;
  }
`;

const SectionTitle = styled.h4`
  margin: 16px 0 4px;
  font-size: 14px;
  color: #333;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: getCompanyCurrency(),
  }).format(value);

type Props = {
  entry: Object,
  onClose: () => void,
};

const JournalEntryDetailModal = ({ entry, onClose }: Props) => {
  const handlePost = async () => {
    try {
      await PostJournalEntryMutation.commit({
        variables: { input: { id: entry.id } },
      });
      Actions.alertNotification(
        i18n.t('Journal entry posted.'),
        'success',
      );
      onClose();
    } catch (e) {
      Actions.alertNotification(
        i18n.t('Error posting journal entry.'),
        'error',
      );
    }
  };

  const handleVoid = async () => {
    try {
      await VoidJournalEntryMutation.commit({
        variables: { input: { id: entry.id } },
      });
      Actions.alertNotification(
        i18n.t('Journal entry voided.'),
        'success',
      );
      onClose();
    } catch (e) {
      Actions.alertNotification(
        i18n.t('Error voiding journal entry.'),
        'error',
      );
    }
  };

  return (
    <Modal isOpen onClose={onClose}>
      <ModalHeader
        header={`${i18n.t('Journal Entry')} ${entry.entryNumber}`}
        onClose={onClose}
      />
      <ModalBody withPadding>
        <DetailGrid>
          <DetailItem>
            <DetailLabel>{i18n.t('Entry Number')}</DetailLabel>
            <DetailValue>{entry.entryNumber}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{i18n.t('Entry Date')}</DetailLabel>
            <DetailValue>
              {moment(entry.entryDate).format('MMMM D, YYYY')}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{i18n.t('Status')}</DetailLabel>
            <DetailValue>
              <StatusBadge status={entry.status}>{entry.status}</StatusBadge>
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{i18n.t('Source')}</DetailLabel>
            <DetailValue>
              {entry.sourceType.replace(/_/g, ' ')}
            </DetailValue>
          </DetailItem>
          {entry.memo && (
            <DetailItem style={{ gridColumn: '1 / -1' }}>
              <DetailLabel>{i18n.t('Memo')}</DetailLabel>
              <DetailValue>{entry.memo}</DetailValue>
            </DetailItem>
          )}
          {entry.createdByUser && (
            <DetailItem>
              <DetailLabel>{i18n.t('Created By')}</DetailLabel>
              <DetailValue>
                {entry.createdByUser.firstName}{' '}
                {entry.createdByUser.lastName}
              </DetailValue>
            </DetailItem>
          )}
          {entry.postedAt && (
            <DetailItem>
              <DetailLabel>{i18n.t('Posted At')}</DetailLabel>
              <DetailValue>
                {moment(entry.postedAt).format('MMMM D, YYYY h:mm A')}
              </DetailValue>
            </DetailItem>
          )}
        </DetailGrid>

        <SectionTitle>{i18n.t('Journal Lines')}</SectionTitle>
        <LinesTable>
          <thead>
            <tr>
              <Th>{i18n.t('Account')}</Th>
              <Th>{i18n.t('Memo')}</Th>
              <Th>{i18n.t('Debit')}</Th>
              <Th>{i18n.t('Credit')}</Th>
            </tr>
          </thead>
          <tbody>
            {entry.lines &&
              entry.lines.map((line, idx) => (
                <tr key={line.id || idx}>
                  <Td>
                    {line.account.accountNumber} - {line.account.name}
                  </Td>
                  <Td>{line.memo || '—'}</Td>
                  <Td>{line.debit > 0 ? formatCurrency(line.debit) : ''}</Td>
                  <Td>
                    {line.credit > 0 ? formatCurrency(line.credit) : ''}
                  </Td>
                </tr>
              ))}
            <TotalRow>
              <Td colSpan={2}>{i18n.t('Totals')}</Td>
              <Td>{formatCurrency(entry.totalDebit)}</Td>
              <Td>{formatCurrency(entry.totalCredit)}</Td>
            </TotalRow>
          </tbody>
        </LinesTable>
      </ModalBody>
      <ModalFooter>
        <ButtonGroup>
          {entry.status === 'DRAFT' && (
            <Button theme="blue" onClick={handlePost}>
              {i18n.t('Post Entry')}
            </Button>
          )}
          {entry.status === 'POSTED' && (
            <Button theme="red" onClick={handleVoid}>
              {i18n.t('Void Entry')}
            </Button>
          )}
          <Button onClick={onClose}>{i18n.t('Close')}</Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  );
};

export default JournalEntryDetailModal;
